import { useState } from 'react';
import { FormControl, FormSelect } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { hide } from './editEquipmentSlice';
import produce from "immer";
import { Equipment } from '../../models/equipment';
import { addEquipment } from './equipmentSlice';

function InnerAddEquipmentModal() {
  const dispatch = useAppDispatch();
  const [model, setModel] = useState({
    id: 0,
    name: '',
    type: 'weapon',
    slot: '',
    setup: '',
    ob: '',
    db: '',
    pb: '',
    abs: '',
    mvs: '',
    weight: '',
    weaponType: 'long blade',
    weaponHands: '1',
    damageType: '',
    damage: '',
    meta: false
  } as ViewModel);

  const handleClose = () => {
    dispatch(hide());
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModel(produce(model, draft => {
      draft.name = e.target.value;
    }));
  }

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setModel(produce(model, draft => {
      draft.type = e.target.value;
      switch (draft.type) {
        case 'weapon':
          draft.abs = '';
          draft.db = '';
          draft.mvs = '';
          draft.slot = '';
          break;
        case 'armor':
          draft.ob = '';
          draft.weaponType = '';
          draft.weaponHands = '';
          draft.damage = '';
          draft.damageType = '';
          break;
        case 'trinket':
        default:
          draft.ob = '';
          draft.abs = '';
          draft.weaponType = '';
          draft.weaponHands = '';
          draft.damage = '';
          draft.damageType = '';
          break;
      }
    }));
  }

  const handleSlotChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setModel(produce(model, draft => {
      draft.slot = e.target.value;
    }));
  }

  const handleSetupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModel(produce(model, draft => {
      draft.setup = e.target.value;
    }));
  }

  const handleObChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModel(produce(model, draft => {
      draft.ob = e.target.value;
    }));
  }

  const handleDbChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModel(produce(model, draft => {
      draft.db = e.target.value;
    }));
  }

  const handlePbChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModel(produce(model, draft => {
      draft.pb = e.target.value;
    }));
  }

  const handleAbsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModel(produce(model, draft => {
      draft.abs = e.target.value;
    }));
  }

  const handleMvsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModel(produce(model, draft => {
      draft.mvs = e.target.value;
    }));
  }

  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModel(produce(model, draft => {
      draft.weight = e.target.value;
    }));
  }

  const handleWeaponTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setModel(produce(model, draft => {
      draft.weaponType = e.target.value;
    }));
  }

  const handleWeaponHandsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setModel(produce(model, draft => {
      draft.weaponHands = e.target.value;
    }));
  }

  const handleDamageTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModel(produce(model, draft => {
      draft.damageType = e.target.value;
    }));
  }

  const handleDamageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModel(produce(model, draft => {
      draft.damage = e.target.value;
    }));
  }

  const handleSave = () => {
    dispatch(addEquipment({
      ...model,
      ob: getInt(model.ob),
      db: getInt(model.db),
      pb: getInt(model.pb),
      abs: getFloat(model.abs),
      mvs: getInt(model.mvs),
      weight: getFloat(model.weight),
      weaponHands: getFloat(model.weaponHands)
    } as Equipment));
  }

  const getInt = (value: string) => {
    return parseInt(value, 10) || 0;
  }

  const getFloat = (value: string) => {
    return parseFloat(value) || 0;
  }

  return (
    <Modal show={true} onHide={() => handleClose()} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-12 mb-2">
            <label className="smaller">Name</label>  
            <FormControl value={model.name ?? ''} onChange={e => handleNameChange(e as React.ChangeEvent<HTMLInputElement>)} />
          </div>
        </div>
        <div className="row">
          <div className="col-6 col-lg-4 mb-2">
            <label className="smaller">Type</label>
            <FormSelect value={model.type ?? ''} onChange={e => handleTypeChange(e as React.ChangeEvent<HTMLSelectElement>)} style={{height: 38, fontSize: '1rem', padding: '0.375rem 0.75rem'}}>
              {!model.type ? <option value=""></option> : null}
              <option value="weapon">weapon</option>
              <option value="armor">armor</option>
              <option value="trinket">trinket</option>
            </FormSelect>
          </div>
          {['armor', 'trinket'].includes(model.type) ? (
            <div className="col-6 col-lg-4 mb-2">
              <label className="smaller">Slot</label>
              <FormSelect value={model.slot ?? ''} onChange={e => handleSlotChange(e as React.ChangeEvent<HTMLSelectElement>)} style={{height: 38, fontSize: '1rem', padding: '0.375rem 0.75rem'}}>
                {!model.slot ? <option value=""></option> : null}
                <option value="finger">finger</option>
                <option value="head">head</option>
                <option value="neck">neck</option>
                <option value="body">body</option>
                <option value="cloak">cloak</option>
                <option value="back">back</option>
                <option value="arms">arms</option>
                <option value="hands">hands</option>
                <option value="wrist">wrist</option>
                <option value="shield">shield</option>
                <option value="waist">waist</option>
                <option value="belt">belt</option>
                <option value="legs">legs</option>
                <option value="feet">feet</option>
              </FormSelect>
            </div>
          ) : null}
          {['weapon'].includes(model.type) ? (
            <div className="col-6 col-lg-4 mb-2">
              <label className="smaller">Weapon Type</label>
              <FormSelect value={model.weaponType ?? ''} onChange={e => handleWeaponTypeChange(e as React.ChangeEvent<HTMLSelectElement>)} style={{height: 38, fontSize: '1rem', padding: '0.375rem 0.75rem'}}>
                {!model.weaponType ? <option value=""></option> : null}
                <option value="long blade">long blade</option>
                <option value="medium blade">medium blade</option>
                <option value="fencing blade">fencing blade</option>
                <option value="short blade">short blade</option>
                <option value="axe">axe</option>
                <option value="club">club</option>
                <option value="staff">staff</option>
                <option value="lance">lance</option>
                <option value="spear">spear</option>
                <option value="javelin">javelin</option>
                <option value="polearm">polearm</option>
                <option value="flail">flail</option>
                <option value="projectile">projectile</option>
              </FormSelect>
            </div>
          ) : null}
          {['weapon'].includes(model.type) ? (
            <div className="col-6 col-lg-4 mb-2">
              <label className="smaller">Weapon Hands</label>
              <FormSelect value={model.weaponHands ?? ''} onChange={e => handleWeaponHandsChange(e as React.ChangeEvent<HTMLSelectElement>)} style={{height: 38, fontSize: '1rem', padding: '0.375rem 0.75rem'}}>
                {!model.weaponHands ? <option value=""></option> : null}
                <option value="1">1</option>
                <option value="1.5">1.5</option>
                <option value="2">2</option>
              </FormSelect>
            </div>
          ) : null}          
          {['weapon'].includes(model.type) ? (
            <div className="col-6 col-lg-4 mb-2">
              <label className="smaller">Damage</label>
              <FormControl value={model.damage ?? ''} onChange={e => handleDamageChange(e as React.ChangeEvent<HTMLInputElement>)} />
            </div>
          ) : null}
          {['weapon'].includes(model.type) ? (
            <div className="col-6 col-lg-4 mb-2">
              <label className="smaller">Damage Type</label>
              <FormControl value={model.damageType ?? ''} onChange={e => handleDamageTypeChange(e as React.ChangeEvent<HTMLInputElement>)} />
            </div>
          ) : null}
          {['weapon'].includes(model.type) ? (
          <div className="col-6 col-lg-4 mb-2">
            <label className="smaller">OB</label>
            <FormControl value={model.ob ?? ''} onChange={e => handleObChange(e as React.ChangeEvent<HTMLInputElement>)} />
          </div>
          ) : null}
          {['armor', 'trinket'].includes(model.type) ? (
            <div className="col-6 col-lg-4 mb-2">
              <label className="smaller">DB</label>
              <FormControl value={model.db ?? ''} onChange={e => handleDbChange(e as React.ChangeEvent<HTMLInputElement>)} />
            </div>
          ) : null}
          {['weapon', 'armor', 'trinket'].includes(model.type) ? (
            <div className="col-6 col-lg-4 mb-2">
              <label className="smaller">PB</label>
              <FormControl value={model.pb ?? ''} onChange={e => handlePbChange(e as React.ChangeEvent<HTMLInputElement>)} />
            </div>
          ) : null}
          {['armor'].includes(model.type) ? (
            <div className="col-6 col-lg-4 mb-2">
              <label className="smaller">ABS</label>
              <FormControl value={model.abs ?? ''} onChange={e => handleAbsChange(e as React.ChangeEvent<HTMLInputElement>)} />
            </div>
          ) : null}
          {['armor', 'trinket'].includes(model.type) ? (
            <div className="col-6 col-lg-4 mb-2">
              <label className="smaller">MVS</label>
              <FormControl value={model.mvs ?? ''} onChange={e => handleMvsChange(e as React.ChangeEvent<HTMLInputElement>)} />
            </div>
          ) : null}
          {['weapon', 'armor', 'trinket'].includes(model.type) ? (
            <div className="col-6 col-lg-4 mb-2">
              <label className="smaller">Weight</label>
              <FormControl value={model.weight ?? ''} onChange={e => handleWeightChange(e as React.ChangeEvent<HTMLInputElement>)} />
            </div>
          ) : null}
          {['weapon', 'armor', 'trinket'].includes(model.type) ? (
            <div className="col-6 col-lg-4 mb-2">
              <label className="smaller">Setup</label>
              <FormControl value={model.setup ?? ''} onChange={e => handleSetupChange(e as React.ChangeEvent<HTMLInputElement>)} />
            </div>
          ) : null}
        </div>        
      </Modal.Body>
      <Modal.Footer>        
        <Button variant="secondary" onClick={() => handleClose()}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => handleSave()}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function AddEquipmentModal() {
  const visible = useAppSelector(state => state.editEquipment.addVisible);  
  return visible ? <InnerAddEquipmentModal/> : null;
}

interface ViewModel {  
  id: number;
  name: string;
  type: string;
  slot: string;
  setup: string;
  ob: string;
  db: string;
  pb: string;
  abs: string;
  mvs: string;
  weight: string;
  weaponType: string;
  weaponHands: string;
  damageType: string;
  damage: string;
  meta: boolean;
}

export default AddEquipmentModal;